import { useSelectorTyped } from "../../../../common/redux/store";
import getManageInfraQuestions from "./data/questions";
import { SurveyModel } from "survey-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ApplicationDetailsPageActions, OktaChicletRequest } from "../redux";
import { FormRef } from "../../../../common/components/forms";
import { prefixApplicationNameToGitHubRepos } from "../../../get-started/data/common-functions";
import { useGetFeatureToggleState } from "../../../../common/hooks/feature-toggles";
import moment from "moment";
import { TeamType } from "../../../create-team/redux";
import { resolveGitsGithubOrganisationForAdditionalRequest } from "./resolve-gits-github-organisation-for-additional-request";
import { resolveGitsGithubOrganisations } from "../../../get-started/data/team-github-orgs";
import { mergeUniqueGithubOrgLists } from "./merge-github-org-lists";

type FormData = {
  githubRepos: Array<{ name: string }>;
  gcpProjects: Array<{ name: string }>;
  oktaChiclets: OktaChicletRequest[];
  githubOrganisation?: string;
};

export default function useManageInfrastructureController() {
  const application = useSelectorTyped((state) => state.applicationDetailsPage.applicationBasicInfoPEV.value!);
  const infrastructureInfoPEV = useSelectorTyped((state) => state.applicationDetailsPage.infrastructureInfoPEV);
  const requestAdditionalInfraPES = useSelectorTyped((state) => state.applicationDetailsPage.requestAdditionalInfraPES);
  const neoSupportEmail = useSelectorTyped((state) => state.header.uiConfigsPEV.value.emails.neoSupportEmail);
  const oktaChicletHelpDocumentLink = useSelectorTyped((state) => state.header.uiConfigsPEV.value.externalLinks.oktaChicletHelpDocumentLink);
  const isGitsTeam = application.team.teamType === TeamType.GITS;
  const [gitsGithubOrganisationChoices, setGitsGithubOrganisationChoices] = useState<string[] | null>(null);
  const {
    productionGCPProjectDisablingDate,
    defaultProjectIdForExperimentAndInnovationApp,
  } = useSelectorTyped((state) => state.header.uiConfigsPEV.value);
  const diSupportEmail = useSelectorTyped((state) => state.header.uiConfigsPEV.value.emails.diSupportEmail);
  const isRequestOktaPreviewToggleEnabled = useGetFeatureToggleState("Neo_Applications_RequestOktaPreview");
  const [showNavigationButtons, setShowNavigationButtons] = useState(false);
  const dispatch = useDispatch();
  const formRef = useRef<FormRef>(null);
  const [formKey, setFormKey] = useState(0);
  const resetFormData = () => {
    formRef.current?.reset();
    dispatch(ApplicationDetailsPageActions.resetRequestAdditionalInfraPES());
  };
  const isDisplayGCPBudgetAlertToggleEnabled = useGetFeatureToggleState("Neo_Applications_DisplayBudgetAlert");
  useEffect(() => {
    if (requestAdditionalInfraPES.success) {
      resetFormData();
    }
  }, [requestAdditionalInfraPES]);


  useEffect(() => {
    if (!isGitsTeam) {
      setGitsGithubOrganisationChoices([]);
      return;
    }
    const infra = infrastructureInfoPEV.value;
    if (!infra) {
      setGitsGithubOrganisationChoices(null);
      return;
    }
    const fromDi = infra.githubOrganisations?.filter((s) => String(s).trim().length > 0) ?? [];
    const teamId = application.team.id;
    if (!teamId) {
      setGitsGithubOrganisationChoices(fromDi);
      return;
    }
    let cancelled = false;
    void (async () => {
      const fromBff = await resolveGitsGithubOrganisations(teamId);
      if (!cancelled) {
        setGitsGithubOrganisationChoices(mergeUniqueGithubOrgLists(fromBff, fromDi));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [application.team.id, infrastructureInfoPEV.value, isGitsTeam]);

  useEffect(() => {
    setFormKey((k) => k + 1);
  }, [
    infrastructureInfoPEV,
    isRequestOktaPreviewToggleEnabled,
    oktaChicletHelpDocumentLink,
    diSupportEmail,
    gitsGithubOrganisationChoices,
  ]);

  const isRequestAdditionalInfraInProgress = () => !!requestAdditionalInfraPES.progress;

  const shouldRenderError = () => !!infrastructureInfoPEV.error;

  const isInfraDetailsFetchedSuccessfully = () => !shouldRenderError() && !infrastructureInfoPEV.progress;

  return {
    neoSupportEmail,
    infrastructureDetailProgress: infrastructureInfoPEV.progress,
    infrastructureDetailError: infrastructureInfoPEV.error,
    shouldRenderError,
    shouldRenderForm: isInfraDetailsFetchedSuccessfully,
    getQuestions: () => getManageInfraQuestions({
      infrastructureInfo: infrastructureInfoPEV.value!,
      teamType: application.team.teamType,
      gitsGithubOrganisationChoices: isGitsTeam ? gitsGithubOrganisationChoices : null,
      applicationName: application.name,
      isRequestOktaPreviewToggleEnabled,
      oktaChicletHelpDocumentLink,
      diSupportEmail,
      isApplicationCreatedAfterProductionGCPDisablingDate: moment(application.createdAt).format("YYYY-MM-DD") >= productionGCPProjectDisablingDate,
      isDisplayGCPBudgetAlertToggleEnabled,
      shouldDisplayBudgetAlert: application.team.teamType === TeamType.EXPERIMENT_AND_INNOVATION
        && application.project?.id === defaultProjectIdForExperimentAndInnovationApp,
    }),
    shouldRenderNavigationButtons: () => showNavigationButtons,
    onFormValueChanged: (form: SurveyModel) => {
      const hasAnyValue = (fieldName: string) => {
        const data = form.getValue(fieldName);
        return data ? (data as unknown[]).length > 0 : false;
      };
      setShowNavigationButtons(
        hasAnyValue("githubRepos")
        || hasAnyValue("gcpProjects")
        || hasAnyValue("oktaChiclets"),
      );
    },
    onSubmit: (formData: FormData) => {
      const repositories = formData.githubRepos
        ? prefixApplicationNameToGitHubRepos(formData.githubRepos, application.name)
        : undefined;
      const selectedOrg = formData.githubOrganisation?.trim();
      const githubOrganisation = isGitsTeam && repositories?.length
        ? (selectedOrg
          || resolveGitsGithubOrganisationForAdditionalRequest({
            isGitsTeam,
            gitsGithubOrganisationChoices: gitsGithubOrganisationChoices ?? [],
            githubOrganisationsFromInfra: infrastructureInfoPEV.value?.githubOrganisations,
            githubOrg: infrastructureInfoPEV.value?.githubOrg,
          }))
        : undefined;
      dispatch(ApplicationDetailsPageActions.requestAdditionalInfrastructure({
        applicationId: application.id,
        data: {
          repositories,
          cloudProjects: formData.gcpProjects?.map(({ name }) => name),
          oktaChiclet: formData.oktaChiclets?.[0],
          ...(githubOrganisation ? { githubOrganisation } : {}),
        },
      }));
    },
    onCancel: () => {
      resetFormData();
    },
    isRequestAdditionalInfraInProgress,
    getSubmitButtonLabel: () => isRequestAdditionalInfraInProgress() ? "Submitting" : "Submit",
    formRef,
    formKey,
    shouldRenderBillingProjectNotAssociatedAlert: () => isInfraDetailsFetchedSuccessfully()
      && infrastructureInfoPEV.value?.canShowBillingProjectNotAssociatedAlert,
  };
}
